import { useEffect, useState } from "react";
import { Download, Share, Plus as PlusIcon, X } from "lucide-react";

const LS_DISMISSED = "bicyclub_install_dismissed_v1";

function isIOS() {
  const ua = navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}
function isStandalone() {
  return window.matchMedia?.("(display-mode: standalone)")?.matches
    || window.navigator?.standalone === true;
}

export default function InstallPWA() {
  const [deferred, setDeferred] = useState(null);
  const [ios, setIos] = useState(false);
  const [showIOSGuide, setShowIOSGuide] = useState(false);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    if (isStandalone()) { setInstalled(true); return; }
    if (isIOS()) setIos(true);
    const onBefore = (e) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => { setInstalled(true); setDeferred(null); };
    window.addEventListener("beforeinstallprompt", onBefore);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBefore);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (installed) return null;
  const dismissed = typeof window !== "undefined" && window.localStorage?.getItem(LS_DISMISSED) === "1";

  // Hide button if user dismissed it earlier
  if (dismissed && !showIOSGuide) return null;
  // Hide if there's no prompt available AND not iOS
  if (!deferred && !ios) return null;

  const install = async () => {
    if (deferred) {
      try {
        deferred.prompt();
        await deferred.userChoice;
      } catch { /* noop */ }
      setDeferred(null);
      return;
    }
    if (ios) setShowIOSGuide(true);
  };

  const dismiss = () => {
    try { window.localStorage?.setItem(LS_DISMISSED, "1"); } catch { /* noop */ }
    setShowIOSGuide(false);
    setDeferred(null);
  };

  return (
    <>
      <button
        type="button"
        onClick={install}
        data-testid="install-pwa-btn"
        className="inline-flex items-center gap-1.5 px-2.5 md:px-3 py-2 border border-[#E11D48]/40 text-[#E11D48] hover:bg-[#E11D48] hover:text-white rounded-sm text-xs uppercase tracking-wider font-bold transition-colors"
        title="Installa Bicyclub come app sul tuo dispositivo"
      >
        <Download className="w-3.5 h-3.5" />
        <span className="hidden sm:inline">Installa app</span>
      </button>

      {showIOSGuide && (
        <div
          className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-3"
          data-testid="install-ios-guide"
          onClick={dismiss}
        >
          <div
            className="bg-white rounded-lg max-w-md w-full p-6 relative shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={dismiss}
              className="absolute top-3 right-3 p-1 text-gray-400 hover:text-gray-900"
              data-testid="install-ios-close"
              aria-label="Chiudi"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-lg bg-[#E11D48] flex items-center justify-center">
                <Download className="w-6 h-6 text-white" />
              </div>
              <div>
                <div className="font-display font-black text-xl uppercase tracking-tighter">
                  Installa Bicy<span className="text-[#E11D48]">club</span>
                </div>
                <div className="text-xs text-gray-500">Aggiungi l'icona alla schermata Home</div>
              </div>
            </div>

            <ol className="space-y-3 text-sm text-gray-800">
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#E11D48] text-white text-xs font-bold flex items-center justify-center">1</span>
                <div className="flex-1">
                  Tocca l'icona <Share className="w-4 h-4 inline text-blue-500 -mt-0.5" /> <strong>"Condividi"</strong> in fondo a Safari
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#E11D48] text-white text-xs font-bold flex items-center justify-center">2</span>
                <div className="flex-1">
                  Scorri e tocca <PlusIcon className="w-4 h-4 inline -mt-0.5" /> <strong>"Aggiungi alla schermata Home"</strong>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#E11D48] text-white text-xs font-bold flex items-center justify-center">3</span>
                <div className="flex-1">
                  Tocca <strong>"Aggiungi"</strong> in alto a destra. L'icona Bicyclub apparirà sulla home come un'app vera.
                </div>
              </li>
            </ol>

            <div className="mt-5 p-3 bg-amber-50 border border-amber-200 rounded-sm text-xs text-amber-900">
              <strong>Funziona solo su Safari.</strong> Se stai usando Chrome su iPhone, apri prima la pagina in Safari.
            </div>

            <button
              type="button"
              onClick={dismiss}
              data-testid="install-ios-dismiss"
              className="mt-5 w-full py-3 text-xs uppercase tracking-wider font-bold text-gray-600 hover:text-gray-900 border border-gray-200 hover:border-gray-400 rounded-sm"
            >
              Ho capito
            </button>
          </div>
        </div>
      )}
    </>
  );
}
